/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Google Tag Manager dataLayer management.
 *
 * Manages window.dataLayer and provides typed push functions. All events push to the array
 * regardless of whether GTM is loaded — events queue up and fire once GTM connects.
 */

export interface DataLayerEvent {
    event?: string;
    event_name?: string;
    element_id?: string;
    ecommerce?: EcommerceData | null;
    [key: string]: unknown;
}

export interface EcommerceItem {
    item_id: string;
    item_name: string;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_variant?: string;
    variation_id?: string;
    price?: number;
    price_before_discount?: number;
    discount?: number;
    quantity?: number;
    delivery_type?: string;
    order_delivery_type?: string;
    position?: number;
    list?: string;
    [key: string]: unknown;
}

export interface EcommerceData {
    items?: EcommerceItem[];
    item_list_id?: string;
    item_list_name?: string;
    transaction_id?: string;
    value?: number;
    tax?: number;
    shipping?: number;
    currency?: string;
    coupon?: string;
    /** GA4 standard event param on `add_shipping_info` — human-readable shipping method label. */
    shipping_tier?: string;
    /** GA4 standard event param on `add_payment_info` — payment method id/label. */
    payment_type?: string;
    total_item_revenue?: number;
    impressions?: EcommerceItem[] | Array<Record<string, unknown>>;
    detail?: { products?: EcommerceItem[] };
    add?: { products?: EcommerceItem[]; updatedBasket?: Record<string, unknown> };
    remove?: { products?: EcommerceItem[]; updatedBasket?: Record<string, unknown> };
    checkout?: { actionField?: Record<string, unknown>; products?: EcommerceItem[] };
    purchase?: { actionField?: Record<string, unknown>; products?: EcommerceItem[] };
    click?: { actionField?: Record<string, unknown>; products?: EcommerceItem[] };
    checkout_option?: { actionField?: Record<string, unknown> };
}

declare global {
    interface Window {
        dataLayer?: DataLayerEvent[];
    }
}

/**
 * Returns window.dataLayer, initializing it if needed.
 * Safe to call in SSR — returns undefined when window is not available.
 */
export function getDataLayer(): DataLayerEvent[] | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }
    if (!window.dataLayer) {
        window.dataLayer = [];
    }
    return window.dataLayer;
}

/**
 * Pushes an event to the dataLayer.
 * No-op when window is not available (SSR).
 */
export function pushEvent(event: DataLayerEvent): void {
    const dataLayer = getDataLayer();
    if (dataLayer) {
        dataLayer.push(event);
    }
}

/**
 * Pushes { ecommerce: null } to reset the ecommerce object.
 * GA4 best practice: clear ecommerce before pushing new ecommerce events.
 */
export function clearEcommerce(): void {
    pushEvent({ ecommerce: null });
}

/**
 * Clears ecommerce, then pushes an ecommerce event with the given data.
 * Ensures GA4 receives a clean ecommerce payload.
 *
 * @param eventName - The specific event name (e.g. "add_to_cart", "virtual_pageview")
 * @param ecommerceData - Ecommerce payload
 * @param additionalData - Extra fields (element_id, page_title, etc.)
 * @param eventType - Optional legacy field: "userInteraction" for user-initiated events,
 *   "nonInteraction" for passive events. When set, used for `event`; `event_name` stays as eventName.
 *   Kept for legacy GTM compatibility until business confirms removal.
 */
export function pushEcommerceEvent(
    eventName: string,
    ecommerceData: EcommerceData,
    additionalData: Omit<DataLayerEvent, 'event' | 'ecommerce'> = {},
    eventType?: 'userInteraction' | 'nonInteraction'
): void {
    clearEcommerce();
    pushEvent({
        event: eventType ?? eventName,
        event_name: eventName,
        storefront_type: 'sfn',
        ...additionalData,
        ecommerce: ecommerceData,
    });
}
